<script>
/**
 * Module scope, deliberately in a plain <script> block: the body of
 * <script setup> is compiled into `setup()` and therefore re-runs for every
 * instance, so a counter declared there would hand all twenty tooltips the id
 * `tooltip-bubble-1` and break `aria-describedby` for all but the first.
 */
let instanceCount = 0
</script>

<script setup>
/**
 * A tooltip that cannot be clipped or covered by its surroundings.
 *
 * WHY THIS EXISTS
 * ---------------
 * `vue3-popper` renders its bubble as a sibling of the trigger, inside whatever
 * ancestors the trigger happens to have. In the orders table that is fatal
 * twice over:
 *
 *   1. CLIPPING. The table wrapper is `overflow-x-auto overflow-y-auto
 *      max-h-[...]`. Any tooltip near an edge is cut off at the scroll box.
 *   2. STACKING. The actions column is pinned with `sticky right-0 z-10`, so
 *      every row's cell is its own stacking context at the same z-index. A
 *      tooltip in row N belongs to row N's context, and row N+1's opaque cell
 *      paints over it. No z-index on the tooltip itself can escape that — it
 *      is capped by the ancestor context it lives in.
 *
 * Teleporting to <body> removes both problems at the source: the bubble has no
 * clipping ancestor and no competing sibling. It is positioned with `fixed`
 * coordinates measured from the trigger.
 *
 * DROP-IN FOR vue3-popper
 * -----------------------
 * Same API as the `<Popper>` usages in this codebase — `content`, `hover`, a
 * default slot for the trigger — and it reads the same `--popper-theme-*`
 * variables from main.css, so nothing changes visually.
 *
 * Not a general-purpose replacement: there is no arrow, and `placement` covers
 * only the four sides. That is all this app uses.
 *
 * WHY IT ALSO OPENS ON TAP
 * ------------------------
 * Hover and focus were the only ways in, and neither exists on a phone. That
 * did not merely degrade decoration — it deleted content. `+N more` on a proxy
 * profile is the only place the 5th and later countries are written down; the
 * orderability hint is the only statement of why a site cannot take votes right
 * now. On touch those strings had no reachable representation at all.
 *
 * The tap path is deliberately additive: every mouse and keyboard branch below
 * exits early when the interaction did not come from a finger, so a desktop
 * session behaves byte-for-byte as it did before.
 */
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'

const props = defineProps({
  /** Tooltip text. Nothing renders when this is empty. */
  content: { type: String, default: '' },
  /**
   * Accepted for API compatibility with vue3-popper. This component opens on
   * hover and focus where those exist, and on tap where they do not.
   */
  hover: { type: Boolean, default: true },
  /** Which side of the trigger to prefer. Flips automatically near a viewport edge. */
  placement: {
    type: String,
    default: 'bottom',
    validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value),
  },
})

const triggerRef = ref(null)
const bubbleRef = ref(null)
const visible = ref(false)
const position = ref({ top: 0, left: 0 })

/**
 * True only while the bubble is being held open by a tap. Hover and focus never
 * set it. Everything that behaves differently for a tapped tooltip — surviving
 * a scroll, accepting pointer events, ignoring `mouseleave` — keys off this one
 * flag rather than off the device, so a hybrid laptop gets hover semantics when
 * it hovers and tap semantics when it taps.
 */
const openedByTap = ref(false)

/** Distance between the trigger and the bubble. */
const GAP = 8
/** Keeps the bubble off the very edge of the viewport when it has to shift. */
const MARGIN = 8

/**
 * `aria-describedby` needs a stable target, and the bubble is teleported, so it
 * cannot be found by DOM proximity. The module counter above is enough: ids
 * only have to be unique within the document, not meaningful.
 */
const bubbleId = `tooltip-bubble-${++instanceCount}`

const hasContent = computed(() => props.content !== null && String(props.content).length > 0)

/**
 * TOUCH DETECTION
 * ---------------
 * `(hover: none), (pointer: coarse)` describes the *primary input* the browser
 * is actually being driven with. A viewport width test cannot: a 380px-wide
 * browser window on a desktop still has a mouse and must keep hover semantics,
 * and a 1024px iPad still has none. User-agent sniffing is worse again — it is
 * a guess about hardware from a string vendors deliberately lie in, and it
 * cannot notice a keyboard case being attached halfway through a session, which
 * the `change` listener below does.
 *
 * This flag is only ever used to *suppress* synthetic hover, never to decide
 * whether a tap counts — that decision is made per-event from `pointerType`,
 * because a hybrid device matches neither query cleanly.
 */
const coarsePointerQuery =
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(hover: none), (pointer: coarse)')
    : null
const isCoarsePointer = ref(coarsePointerQuery ? coarsePointerQuery.matches : false)

function onPointerCapabilityChange(event) {
  isCoarsePointer.value = event.matches
}

/**
 * Measures the trigger and places the bubble, flipping to the opposite side
 * when the preferred one would go off-screen.
 *
 * Runs after the bubble is in the DOM, because its size is needed to centre it
 * and to know whether it fits.
 */
function place() {
  const trigger = triggerRef.value
  const bubble = bubbleRef.value
  if (!trigger || !bubble) return

  const t = trigger.getBoundingClientRect()
  const b = bubble.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight

  let side = props.placement

  // Flip if the preferred side does not fit; if neither fits, keep the
  // preferred one and let the clamp below do what it can.
  if (side === 'bottom' && t.bottom + GAP + b.height > vh && t.top - GAP - b.height > 0) side = 'top'
  else if (side === 'top' && t.top - GAP - b.height < 0 && t.bottom + GAP + b.height < vh) side = 'bottom'
  else if (side === 'right' && t.right + GAP + b.width > vw && t.left - GAP - b.width > 0) side = 'left'
  else if (side === 'left' && t.left - GAP - b.width < 0 && t.right + GAP + b.width < vw) side = 'right'

  let top
  let left

  if (side === 'top' || side === 'bottom') {
    top = side === 'bottom' ? t.bottom + GAP : t.top - b.height - GAP
    left = t.left + t.width / 2 - b.width / 2
  } else {
    left = side === 'right' ? t.right + GAP : t.left - b.width - GAP
    top = t.top + t.height / 2 - b.height / 2
  }

  // Keep it fully on screen. `fixed` coordinates are viewport-relative, so this
  // is a straight clamp.
  left = Math.min(Math.max(left, MARGIN), Math.max(MARGIN, vw - b.width - MARGIN))
  top = Math.min(Math.max(top, MARGIN), Math.max(MARGIN, vh - b.height - MARGIN))

  position.value = { top, left }
}

async function show() {
  if (!hasContent.value) return
  if (!visible.value) bindWhileOpen()
  visible.value = true
  // The bubble has to exist before it can be measured.
  await nextTick()
  place()
}

function hide() {
  if (visible.value) unbindWhileOpen()
  visible.value = false
  openedByTap.value = false
  cancelFollow()
}

/* ------------------------------------------------------------------ *
 * Pointer and keyboard entry points
 * ------------------------------------------------------------------ */

/**
 * Mobile browsers replay a whole synthetic mouse sequence after a tap —
 * `mouseover`, `mouseenter`, `mousedown`, `mouseup`, `click` — for anything
 * they consider clickable. Left alone, `mouseenter` would open the bubble and
 * the `click` a few milliseconds later would read that as "already open" and
 * shut it again, so a tap would visibly do nothing. Refusing synthetic hover
 * outright on a coarse-pointer device is the cheap half of the fix; the other
 * half is in `onTriggerClick`, which never blind-toggles.
 */
function onHoverIn() {
  if (isCoarsePointer.value) return
  show()
}

function onHoverOut() {
  // A tapped bubble is not chasing the cursor. Synthetic `mouseleave` after a
  // tap, or a stray mouse crossing on a hybrid machine, must not close what the
  // user deliberately opened; only the three explicit dismissals do that.
  if (openedByTap.value) return
  hide()
}

function onFocusOut() {
  // Not guarded by `openedByTap` on purpose. When the slot content is a real
  // button, tapping it both opens this bubble and runs the button's own
  // handler; if that handler moves focus (a modal, a route change) the bubble
  // is now describing something the user has left, and blur is the earliest
  // honest signal to drop it.
  hide()
}

/**
 * A tap opens the bubble.
 *
 * EVENT STRATEGY — and why it cannot eat a button's click.
 *
 * This is an ordinary bubble-phase `click` listener on the wrapper. It calls
 * neither `preventDefault()` nor `stopPropagation()`, so it is physically
 * incapable of consuming the event: by the time it runs, an inner control has
 * already had the event at the target phase, and an outer control still gets it
 * afterwards. That matters most for `JbButton`, where this component is
 * rendered *inside* the `<button>` — a `stopPropagation()` here would silently
 * kill every tooltip-bearing button in the app.
 *
 * `.capture` was rejected. It would let us see the clicks that call sites
 * suppress with `@click.stop` (`OrderStatusBadge`'s fix-issue chip), but those
 * `.stop`s are a deliberate statement that the click belongs to that control —
 * they exist to keep a row-level handler from also firing. Reading an event a
 * call site went out of its way to contain, in order to pop a label over the
 * dialog that click just opened, is not a feature. Bubble phase means those
 * sites opt out of tap-to-open for free, with no edit to the call site.
 *
 * The `closest()` guard covers the mirror image: when the *trigger* sits inside
 * a button or link, the tooltip is that control's label, and the tap is the
 * user pressing it. `InvoicesTable`'s icon-only actions are exactly this — the
 * tap downloads the CSV, and stamping "Download CSV" over the screen after the
 * download already started is noise, not help. Those call sites already carry
 * `aria-label`, which is the correct home for a control's name anyway.
 */
function onTriggerClick(event) {
  const trigger = triggerRef.value
  // Bail before touching `openedByTap`: an empty `content` renders no bubble,
  // and leaving the flag set would make `mouseleave` a no-op for a tooltip that
  // was never open.
  if (!trigger || !hasContent.value) return

  // Browsers that dispatch `click` as a PointerEvent tell us the modality
  // directly, which beats any device-level guess on a hybrid machine. Where
  // they do not (older Firefox), fall back to the media query. Keyboard-
  // synthesised clicks report an empty `pointerType` and land in the fallback,
  // where a fine pointer correctly declines to toggle — focus already opened it.
  const pointerType = event.pointerType
  const fromTouch = pointerType ? pointerType !== 'mouse' : isCoarsePointer.value
  if (!fromTouch) return

  // The tap belongs to a control wrapping us; let it act alone.
  if (trigger.closest('button, a[href], [role="button"], summary')) return

  // Not a blind toggle. On a hybrid device the synthetic `mouseenter` may
  // already have opened the bubble before this click arrives; treating that as
  // "open, therefore close" would make the first tap a no-op. Only a bubble the
  // user themselves tapped open is a bubble a second tap should close.
  if (visible.value && openedByTap.value) {
    hide()
    return
  }

  openedByTap.value = true
  show()
}

/**
 * Anything outside the trigger dismisses. Capture phase so a call site's
 * `@click.stop` cannot strand an open bubble, and `pointerdown` rather than
 * `click` so the dismissal is already done by the time the tapped element runs
 * its own handler. Nothing is prevented or stopped here either — the outside
 * tap still does whatever it was going to do.
 *
 * The bubble itself is deliberately *not* exempt: it holds plain text with
 * nothing to interact with, so a tap on it reads as "done reading". This also
 * means one open tooltip closes when another is tapped, without any shared
 * registry between instances.
 */
function onDocumentPointerDown(event) {
  const trigger = triggerRef.value
  if (trigger && event.target instanceof Node && trigger.contains(event.target)) return
  hide()
}

function onDocumentKeydown(event) {
  if (event.key !== 'Escape' && event.key !== 'Esc') return
  hide()
  // Not stopped: a dialog underneath may want Escape too, and swallowing it
  // would make a tooltip that happens to be open change what Escape means.
}

/* ------------------------------------------------------------------ *
 * Scroll behaviour
 * ------------------------------------------------------------------ */

let followFrame = 0

function cancelFollow() {
  if (!followFrame) return
  cancelAnimationFrame(followFrame)
  followFrame = 0
}

/**
 * Scroll invalidates the measured position, because the bubble is teleported
 * and `fixed` — it does not travel with the trigger. There are two right
 * answers, one per opening mode:
 *
 * HOVER: hide, unchanged. The pointer has left the thing being described, the
 * bubble was incidental, and re-measuring every scroll frame costs more than a
 * hover tooltip is worth.
 *
 * TAP: follow. Dismiss-on-scroll is actively hostile to a tapped tooltip —
 * on a touch screen the same finger that taps routinely nudges the page a few
 * pixels, so the bubble the user just asked for would vanish before it could be
 * read, which is the original bug wearing a different hat. Re-placing inside a
 * rAF is one layout read per frame for one element and only while a tooltip is
 * open, which is affordable; the hide only comes back when the trigger has
 * actually left the viewport and the bubble would otherwise be pointing at
 * nothing.
 *
 * `capture: true` so scrolls inside the table wrapper are seen too, not just
 * the window.
 */
function onScrollOrResize() {
  if (!visible.value) return

  if (!openedByTap.value) {
    hide()
    return
  }

  if (followFrame) return
  followFrame = requestAnimationFrame(() => {
    followFrame = 0
    if (!visible.value) return

    const trigger = triggerRef.value
    if (!trigger) return
    const t = trigger.getBoundingClientRect()
    const offScreen =
      t.bottom < 0 || t.top > window.innerHeight || t.right < 0 || t.left > window.innerWidth
    if (offScreen) {
      hide()
      return
    }

    place()
  })
}

/* ------------------------------------------------------------------ *
 * Listener lifecycle — same bind()/unbind() shape as before
 * ------------------------------------------------------------------ */

/** Document-level listeners exist only while this instance is showing, so 20 idle tooltips cost nothing. */
function bindWhileOpen() {
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
  document.addEventListener('keydown', onDocumentKeydown)
}

function unbindWhileOpen() {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
  document.removeEventListener('keydown', onDocumentKeydown)
}

function bind() {
  window.addEventListener('scroll', onScrollOrResize, true)
  window.addEventListener('resize', onScrollOrResize)
  if (!coarsePointerQuery) return
  if (coarsePointerQuery.addEventListener) {
    coarsePointerQuery.addEventListener('change', onPointerCapabilityChange)
  } else if (coarsePointerQuery.addListener) {
    // Safari < 14 never got the EventTarget interface on MediaQueryList.
    coarsePointerQuery.addListener(onPointerCapabilityChange)
  }
}

function unbind() {
  window.removeEventListener('scroll', onScrollOrResize, true)
  window.removeEventListener('resize', onScrollOrResize)
  // A component can be torn down mid-tooltip — a route change from the very
  // button this tooltip labels does exactly that — so the open-only listeners
  // have to be dropped here too, not only in hide().
  unbindWhileOpen()
  cancelFollow()
  if (!coarsePointerQuery) return
  if (coarsePointerQuery.removeEventListener) {
    coarsePointerQuery.removeEventListener('change', onPointerCapabilityChange)
  } else if (coarsePointerQuery.removeListener) {
    coarsePointerQuery.removeListener(onPointerCapabilityChange)
  }
}

bind()
onBeforeUnmount(unbind)
</script>

<template>
  <!--
    ARIA: still `role="tooltip"`, not a disclosure.

    A tapped bubble is mechanically a disclosure — it persists, and it is
    dismissed rather than escaped from. But `aria-expanded` belongs on the
    control that owns the expanded region, and this wrapper is a bare <span>
    with no role; putting state on it would either be dropped by the AT or, at
    the call sites that slot in a real <button>, contradict the semantics that
    button already has. The bubble's content is also a description of the
    trigger in every usage here, never a region of its own, which is exactly
    what `role="tooltip"` means.

    What was genuinely missing is the association: nothing linked the trigger to
    the teleported bubble, so the text existed only visually. `aria-describedby`
    fixes that. It is set on the wrapper rather than pushed onto the slotted
    child, because writing the attribute onto an element the call site owns
    would clobber any `aria-describedby` already there.
  -->
  <span
    ref="triggerRef"
    class="tooltip-trigger"
    :aria-describedby="visible && hasContent ? bubbleId : null"
    @mouseenter="onHoverIn"
    @mouseleave="onHoverOut"
    @focusin="show"
    @focusout="onFocusOut"
    @click="onTriggerClick"
  >
    <slot />

    <Teleport to="body">
      <span
        v-if="visible && hasContent"
        :id="bubbleId"
        ref="bubbleRef"
        class="tooltip-bubble"
        :class="{ 'tooltip-bubble--tapped': openedByTap }"
        role="tooltip"
        :style="{ top: `${position.top}px`, left: `${position.left}px` }"
      >{{ content }}</span>
    </Teleport>
  </span>
</template>

<style scoped>
/*
 * inline-flex rather than inline-block: vue3-popper's inline-block wrapper was
 * what stopped `w-full` buttons centring, which JbButton works around by
 * skipping the wrapper entirely when there is no tooltip.
 */
.tooltip-trigger {
  display: inline-flex;
}

.tooltip-bubble {
  position: fixed;
  /*
   * Above everything the app itself stacks. The sticky table header is 30 and
   * the actions dropdown is 9998; this sits above both, and being teleported to
   * <body> means the value is compared against top-level elements rather than
   * against siblings inside a table cell.
   */
  z-index: 10000;
  max-width: 20rem;
  width: max-content;
  /*
   * Transparent to the pointer while hovering: the bubble sits within 8px of
   * the trigger, and a bubble that could take the cursor would fire
   * `mouseleave` on the trigger the moment it appeared, hiding itself and
   * flickering forever.
   */
  pointer-events: none;
  white-space: normal;
  font-size: 0.8125rem;
  line-height: 1.35;
  /* The same custom properties vue3-popper read, so the look is unchanged. */
  background: var(--popper-theme-background-color, rgb(55 65 81));
  color: var(--popper-theme-text-color, #fff);
  border-radius: var(--popper-theme-border-radius, 6px);
  padding: var(--popper-theme-padding, 13px);
  box-shadow: var(--popper-theme-box-shadow, 0 6px 30px -6px rgba(0, 0, 0, 0.25));
}

/*
 * A tapped bubble takes pointer events instead. There is no flicker to avoid —
 * nothing re-runs on touch — and `pointer-events: none` would let a tap on the
 * bubble fall through to whatever it is covering, which in the orders table is
 * another row's action button. Silently firing a control the user could not
 * even see is a worse outcome than spending one tap to dismiss, and dismissing
 * is what that tap does (see `onDocumentPointerDown`).
 */
.tooltip-bubble--tapped {
  pointer-events: auto;
}
</style>
